import { useState, useEffect } from 'react';
import { useNavigation } from '@refinedev/core';
import { List } from '@refinedev/antd';
import { Table, Button, Tag, Space, Avatar, Input, message, Popconfirm } from 'antd';
import {
  PlusOutlined,
  BankOutlined,
  EyeOutlined,
  SearchOutlined,
  StopOutlined,
  CheckOutlined,
} from '@ant-design/icons';
import { apiClient } from '../../lib/api-client';

interface BusCompanyStaff {
  id: number;
  name: string;
  legalName: string;
  username: string;
  contactPhone: string;
  contactEmail: string | null;
  status: 'ONLINE' | 'OFFLINE' | 'BLOCKED';
  commissionPercent: number;
  balance: number;
  createdAt: string;
  _count?: {
    busTrips: number;
  };
}

const statusColors: Record<string, string> = {
  ONLINE: 'green',
  OFFLINE: 'orange',
  BLOCKED: 'red',
};

const statusLabels: Record<string, string> = {
  ONLINE: 'Faol',
  OFFLINE: 'Oflayn',
  BLOCKED: 'Bloklangan',
};

export const BusStaffList = () => {
  const { create, show } = useNavigation();
  const [data, setData] = useState<BusCompanyStaff[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  const fetchCompanies = async () => {
    setLoading(true);
    try {
      const response = await apiClient.get('/admin/bus/companies', {
        params: { search: search || undefined },
      });
      setData(response.data.data || []);
    } catch {
      message.error("Ma'lumotlarni yuklashda xatolik");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCompanies();
  }, []);

  const handleSearch = () => {
    fetchCompanies();
  };

  const handleBlock = async (id: number) => {
    try {
      await apiClient.post(`/admin/bus/companies/${id}/block`, { reason: 'Admin tomonidan bloklangan' });
      message.success('Kompaniya bloklandi');
      fetchCompanies();
    } catch {
      message.error('Xatolik yuz berdi');
    }
  };

  const handleUnblock = async (id: number) => {
    try {
      await apiClient.post(`/admin/bus/companies/${id}/unblock`);
      message.success('Kompaniya faollashtirildi');
      fetchCompanies();
    } catch {
      message.error('Xatolik yuz berdi');
    }
  };

  const columns = [
    {
      title: 'Kompaniya',
      key: 'company',
      render: (_: unknown, record: BusCompanyStaff) => (
        <Space>
          <Avatar
            icon={<BankOutlined />}
            style={{
              backgroundColor:
                record.status === 'BLOCKED' ? '#E74C3C' :
                record.status === 'ONLINE' ? '#27AE60' : '#95A5A6',
            }}
          />
          <div>
            <div style={{ fontWeight: 500 }}>{record.name}</div>
            <div style={{ color: '#7F8C8D', fontSize: 12 }}>{record.legalName}</div>
          </div>
        </Space>
      ),
    },
    {
      title: 'Login',
      dataIndex: 'username',
      key: 'username',
      render: (username: string) => (
        <code style={{ background: '#f5f5f5', padding: '2px 8px', borderRadius: 4 }}>
          {username}
        </code>
      ),
    },
    {
      title: 'Telefon',
      dataIndex: 'contactPhone',
      key: 'contactPhone',
    },
    {
      title: 'Komissiya',
      dataIndex: 'commissionPercent',
      key: 'commissionPercent',
      render: (val: number) => `${val}%`,
      width: 100,
    },
    {
      title: 'Reyslar',
      key: 'trips',
      render: (_: unknown, record: BusCompanyStaff) => record._count?.busTrips ?? 0,
      width: 80,
    },
    {
      title: 'Holat',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => (
        <Tag color={statusColors[status]}>{statusLabels[status]}</Tag>
      ),
      width: 110,
    },
    {
      title: 'Yaratilgan',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date: string) => new Date(date).toLocaleDateString('uz-UZ'),
      width: 110,
    },
    {
      title: 'Amallar',
      key: 'actions',
      width: 140,
      render: (_: unknown, record: BusCompanyStaff) => (
        <Space>
          <Button
            type="text"
            icon={<EyeOutlined />}
            onClick={() => show('admin/bus/companies', record.id)}
          />
          {record.status === 'BLOCKED' ? (
            <Popconfirm
              title="Kompaniyani faollashtirasizmi?"
              onConfirm={() => handleUnblock(record.id)}
            >
              <Button type="text" icon={<CheckOutlined />} style={{ color: '#27AE60' }} />
            </Popconfirm>
          ) : (
            <Popconfirm
              title="Kompaniyani bloklaysizmi?"
              onConfirm={() => handleBlock(record.id)}
            >
              <Button type="text" icon={<StopOutlined />} danger />
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  return (
    <List
      title="Xodimlar (Kompaniya profillari)"
      headerButtons={
        <Space>
          <Input
            placeholder="Qidirish..."
            prefix={<SearchOutlined />}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onPressEnter={handleSearch}
            style={{ width: 220 }}
            allowClear
          />
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => create('bus/staff')}
          >
            Yangi xodim
          </Button>
        </Space>
      }
    >
      <Table
        dataSource={data}
        columns={columns}
        rowKey="id"
        loading={loading}
        pagination={{ pageSize: 20, showTotal: (total) => `Jami: ${total}` }}
      />
    </List>
  );
};
